import { useEffect, useState, useCallback } from "react";
import { FolderSimple, UploadSimple, Trash, DownloadSimple, SpinnerGap, File as FileIcon, LinkSimple } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useWorkspace } from "@/context/WorkspaceContext";

function MediaThumb({ item }) {
  const [url, setUrl] = useState(null);
  const isImage = (item.content_type || "").startsWith("image/");
  const isVideo = (item.content_type || "").startsWith("video/");
  useEffect(() => {
    let revoke;
    if (isImage || isVideo) {
      api.get(`/media/${item.id}/raw`, { responseType: "blob" }).then((r) => {
        const u = URL.createObjectURL(r.data); revoke = u; setUrl(u);
      }).catch(() => {});
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [item.id, isImage, isVideo]);

  if (isVideo && url) return <video src={url} muted loop playsInline onMouseOver={(e) => e.target.play()} onMouseOut={(e) => e.target.pause()} className="w-full aspect-square object-cover bg-black" />;
  if (isImage && url) return <img src={url} alt={item.original_filename} className="w-full aspect-square object-cover" />;
  if (isImage || isVideo) return <div className="w-full aspect-square bg-secondary/40 animate-pulse" />;
  return (
    <div className="w-full aspect-square bg-secondary/40 flex flex-col items-center justify-center gap-2">
      <FileIcon size={36} className="text-muted-foreground" />
      <span className="text-[10px] font-mono uppercase text-muted-foreground">{(item.original_filename || "").split(".").pop()}</span>
    </div>
  );
}

export default function MediaLibrary() {
  const { activeId } = useWorkspace();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [attachFor, setAttachFor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [attaching, setAttaching] = useState(null);

  const openAttach = async (item) => {
    setAttachFor(item);
    try {
      const { data } = await api.get(`/posts?workspace_id=${activeId}`);
      setPosts(data);
    } catch { setPosts([]); }
  };

  const attachClip = async (postId) => {
    setAttaching(postId);
    try {
      await api.post(`/posts/${postId}/attach-clip`, { media_id: attachFor.id });
      toast.success("Clip attached to post");
      setAttachFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    } finally { setAttaching(null); }
  };

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/media?workspace_id=${activeId}`);
      setItems(data);
    } finally { setLoading(false); }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("workspace_id", activeId);
      fd.append("file", file);
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Uploaded to your library");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const download = async (item) => {
    try {
      const r = await api.get(`/media/${item.id}/raw`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = item.original_filename || "file";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      setItems((it) => it.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <AppLayout>
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Media Library</h1>
          <p className="text-muted-foreground mt-2">Upload and manage your brand assets — logos, photos and files to reuse across posts.</p>
        </div>
        <label>
          <input type="file" className="hidden" data-testid="media-upload-input" onChange={onUpload} />
          <Button asChild disabled={uploading} data-testid="media-upload-btn">
            <span className="cursor-pointer">
              {uploading ? <SpinnerGap size={18} className="animate-spin mr-2" /> : <UploadSimple size={18} className="mr-2" />}
              Upload file
            </span>
          </Button>
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><SpinnerGap size={28} className="animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-16 text-center" data-testid="media-empty">
          <FolderSimple size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-heading font-bold text-lg">Your library is empty</p>
          <p className="text-muted-foreground mt-1">Upload logos, images or any file to reuse in your content.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {items.map((item) => (
            <div key={item.id} data-testid={`media-item-${item.id}`} className="group relative rounded-lg overflow-hidden border border-border bg-card">
              <MediaThumb item={item} />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {(item.content_type || "").startsWith("video/") && (
                  <Button size="icon" variant="secondary" data-testid={`media-attach-${item.id}`} onClick={() => openAttach(item)} title="Attach to post">
                    <LinkSimple size={16} />
                  </Button>
                )}
                <Button size="icon" variant="secondary" data-testid={`media-download-${item.id}`} onClick={() => download(item)} title="Download">
                  <DownloadSimple size={16} />
                </Button>
                <Button size="icon" variant="destructive" data-testid={`media-delete-${item.id}`} onClick={() => remove(item.id)} title="Delete">
                  <Trash size={16} />
                </Button>
              </div>
              <div className="p-2">
                <p className="text-xs truncate">{item.original_filename}</p>
                <Badge variant="secondary" className="text-[10px] mt-1">{Math.max(1, Math.round((item.size || 0) / 1024))} KB</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!attachFor} onOpenChange={(o) => !o && setAttachFor(null)}>
        <DialogContent data-testid="attach-clip-dialog">
          <DialogHeader>
            <DialogTitle>Attach clip to a post</DialogTitle>
            <DialogDescription>Choose which social post this AI clip should be attached to.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {posts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No posts yet. Repurpose a pack first.</p>
            ) : posts.map((p) => (
              <button key={p.id} data-testid={`attach-post-${p.id}`} onClick={() => attachClip(p.id)} disabled={attaching === p.id}
                className="w-full text-left rounded-md border border-border p-3 hover:bg-accent transition-colors flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium capitalize">{p.platform} {p.language ? `· ${p.language}` : ""}</p>
                  <p className="text-xs text-muted-foreground truncate">{(p.body || "").slice(0, 70)}</p>
                </div>
                {attaching === p.id ? <SpinnerGap size={16} className="animate-spin shrink-0" /> : <LinkSimple size={16} className="shrink-0 text-muted-foreground" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
